import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";

/** Ein Formularfeld: Bezeichnung über dem Steuerelement, Fehler und Hinweis darunter. */
export function Field({ label, error, hint, wide = false, children }: {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`pf-field${wide ? " pf-field--wide" : ""}`}>
      {label ? <span className="pf-field__label">{label}</span> : null}
      {children}
      {error ? <span className="pf-field__error">{error}</span> : null}
      {hint ? <span className="pf-field__hint">{hint}</span> : null}
    </label>
  );
}

/** Ein Wert zum Lesen (kein Formularfeld): Bezeichnung über dem Wert, der Wert
 *  als EIN Element — in der Flex-Spalte von `.pf-field` würde sonst jeder
 *  Text- und `<code>`-Knoten eine eigene Zeile. */
export function Info({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="pf-field">
      <span className="pf-field__label">{label}</span>
      <span className="pf-field__value">{children}</span>
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean };

export function Input({ invalid = false, className, ...rest }: InputProps) {
  return <input className={`pf-input${invalid ? " pf-input--invalid" : ""}${className ? ` ${className}` : ""}`} type="text" {...rest} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`pf-input${className ? ` ${className}` : ""}`} {...rest} />;
}

/** Eine Checkbox mit Text und optionalem Hinweis (`pf-check`). */
export function Check({ checked, onChange, label, hint, disabled = false, name, value, ariaLabel }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  name?: string;
  value?: string;
  ariaLabel?: string;
}) {
  return (
    <label className="pf-check">
      <input
        type="checkbox" name={name} value={value} checked={checked} disabled={disabled} aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="pf-check__text">
        <span>{label}</span>
        {hint ? <span className="pf-check__hint">{hint}</span> : null}
      </span>
    </label>
  );
}
